import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { addMonths } from 'date-fns';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { bookEditionsQueryOptions } from '@/features/product/api/product.api';
import { formatDate } from '@/utils/date';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { promoItemTypesQueryOptions } from '../api/promotion.api';
import { LOOKUP_PAGE_SIZE } from '../constants/promotion.constants';
import { useSavePromoItem, useSavePromoItemType } from '../hooks/usePromoMutations';
import {
  promoItemFormSchema,
  promoItemTypeFormSchema,
  type PromoItem,
  type PromoItemFormValues,
  type PromoItemType,
  type PromoItemTypeFormValues,
} from '../types/promotion.types';

const LOOKUP_PARAMS = { page: 1, pageSize: LOOKUP_PAGE_SIZE, isActive: true } as const;

const typeDefaults = (): PromoItemTypeFormValues => ({
  name: '',
  code: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: formatDate(new Date()),
  activeTo: formatDate(addMonths(new Date(), DEFAULT_ACTIVE_MONTHS)),
});

const DEFAULT_ACTIVE_MONTHS = 3;

const itemDefaults = (): PromoItemFormValues => ({
  name: '',
  code: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: formatDate(new Date()),
  activeTo: formatDate(addMonths(new Date(), DEFAULT_ACTIVE_MONTHS)),
  isView: false,
  isTabView: false,
});

const isoDate = (value: string | null): string => (value === null ? '' : value.slice(0, 10));

const numberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

interface TypeModalProps {
  itemType: PromoItemType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromoItemTypeFormModal({ itemType, open, onOpenChange }: TypeModalProps) {
  const form = useForm<PromoItemTypeFormValues>({
    resolver: zodResolver(promoItemTypeFormSchema),
    defaultValues: typeDefaults(),
  });

  useEffect(() => {
    form.reset(
      itemType === null
        ? typeDefaults()
        : {
            name: itemType.name,
            code: itemType.code ?? '',
            description: itemType.description ?? '',
            slNum: itemType.slNum === null ? '' : String(itemType.slNum),
            isActive: itemType.isActive,
            activeFrom: isoDate(itemType.activeFrom),
            activeTo: isoDate(itemType.activeTo),
          },
    );
  }, [itemType, open, form]);

  const save = useSavePromoItemType(() => onOpenChange(false));

  function submit(values: PromoItemTypeFormValues) {
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: itemType?.id ?? null,
      payload: {
        name: values.name,
        code: values.code,
        isActive: values.isActive,
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{itemType === null ? 'Add Item Type' : 'Edit Item Type'}</DialogTitle>
          <DialogDescription>
            A type groups the promotional items the field hands out.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Umbrella" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="U0001"
                      autoComplete="off"
                      className="tabular-nums"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="activeFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active From</FormLabel>
                    <FormControl>
                      <DatePickerInput value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="activeTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active To</FormLabel>
                    <FormControl>
                      <DatePickerInput value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="slNum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sl Num</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Optional"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={field.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => field.onChange(true)}
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        variant={!field.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => field.onChange(false)}
                      >
                        Inactive
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : itemType === null ? (
                  'Create'
                ) : (
                  'Update'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

interface ItemModalProps {
  item: PromoItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromoItemFormModal({ item, open, onOpenChange }: ItemModalProps) {
  const form = useForm<PromoItemFormValues>({
    resolver: zodResolver(promoItemFormSchema),
    defaultValues: itemDefaults(),
  });

  const { data: types, isLoading: typesLoading } = useQuery(
    promoItemTypesQueryOptions(LOOKUP_PARAMS),
  );
  const { data: editions, isLoading: editionsLoading } = useQuery(
    bookEditionsQueryOptions(LOOKUP_PARAMS),
  );

  useEffect(() => {
    form.reset(
      item === null
        ? itemDefaults()
        : {
            name: item.name,
            code: item.code ?? '',
            description: item.description ?? '',
            slNum: item.slNum === null ? '' : String(item.slNum),
            isActive: item.isActive,
            activeFrom: isoDate(item.activeFrom),
            activeTo: isoDate(item.activeTo),
            isView: item.isView,
            isTabView: item.isTabView,
            ...(item.itemTypeId === null ? {} : { itemTypeId: item.itemTypeId }),
            ...(item.bookEditionNo === null ? {} : { bookEditionNo: item.bookEditionNo }),
          },
    );
  }, [item, open, form]);

  const save = useSavePromoItem(() => onOpenChange(false));

  const typeOptions = (types?.data ?? []).map((type) => ({ id: type.id, name: type.name }));

  const editionOptions = (editions?.data ?? [])
    .filter((edition) => edition.legacyId !== null)
    .map((edition) => ({ id: edition.legacyId!, name: edition.name }));

  function submit(values: PromoItemFormValues) {
    const slNum = numberOrUndefined(values.slNum);
    save.mutate({
      id: item?.id ?? null,
      payload: {
        name: values.name,
        code: values.code,
        isActive: values.isActive,
        isView: values.isView,
        isTabView: values.isTabView,
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.itemTypeId === undefined ? {} : { itemTypeId: values.itemTypeId }),
        ...(values.bookEditionNo === undefined ? {} : { bookEditionNo: values.bookEditionNo }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {item === null ? 'Add Promotional Item' : 'Edit Promotional Item'}
          </DialogTitle>
          <DialogDescription>The item the field hands over on a DCR.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Promotion Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Desk Calendar-2026" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Item Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="GCC25-26"
                      autoComplete="off"
                      className="tabular-nums"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="itemTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <OptionSelect
                      options={typeOptions}
                      isLoading={typesLoading}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="No type"
                      clearable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bookEditionNo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Book Edition</FormLabel>
                  <FormControl>
                    <OptionSelect
                      options={editionOptions}
                      isLoading={editionsLoading}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="No edition"
                      clearable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Item Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="activeFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active From</FormLabel>
                    <FormControl>
                      <DatePickerInput value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="activeTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active To</FormLabel>
                    <FormControl>
                      <DatePickerInput value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="slNum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sl num</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Optional"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={field.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => field.onChange(true)}
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        variant={!field.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => field.onChange(false)}
                      >
                        Inactive
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isView"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        className="mt-0.5"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">Is View</span>
                        <span className="block text-xs text-muted-foreground">
                          Lists the item on Market Promotion Approval and its project filters.
                        </span>
                      </span>
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isTabView"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        className="mt-0.5"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">Is Tab View</span>
                        <span className="block text-xs text-muted-foreground">
                          Gives the item its own tab on the promotion dashboards.
                        </span>
                      </span>
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : item === null ? (
                  'Create'
                ) : (
                  'Update'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
